import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Input, InputNumber, message, Select, Spin } from 'antd';
import { debounce, isEmpty, toInteger } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd/lib/form';

import { getTransferTypes } from '~/api/beneficiaries';
import PhoneInput from '~/components/PhoneInput';
import { PaymentServicesResponse } from '~/types';
import { CountryInput, Form } from '~/components';
import { phoneValidation, regexValidation } from '~/utils';
import DateInput from '../../components/inputs/DateInput';

interface Props {
  form?: FormInstance;
  source: (string | number)[];
}

const TransferTypeForm: FC<Props> = (props) => {
  const { form: formFromProps, source } = props;
  const [t] = useTranslation('payouts');
  const [form] = Form.useForm(formFromProps);

  const [paymentServiceId, setPaymentServiceId] = useState(
    form.getFieldValue([...source, 'serviceId']) || ''
  );
  const [data, setData] = useState<PaymentServicesResponse>({
    cursors: {
      next: undefined,
    },
    services: [],
  });
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce(async (name?: string) => {
      setLoading(true);
      try {
        const res = await getTransferTypes({ name });
        setData(res.data);
      } catch (error: any) {
        void message.error(error.response?.data?.message);
      }
      setLoading(false);
    }, 500),
    []
  );

  const loadMore = async () => {
    setLoading(true);
    try {
      const res = await getTransferTypes({ next: data.cursors?.next });
      setData((prevStae) => ({
        cursors: res.data.cursors,
        services: prevStae.services.concat(res.data.services),
      }));
    } catch (error: any) {
      void message.error(error.response?.data?.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    void handleSearch();
  }, [handleSearch]);

  const onScroll = (e: any) => {
    const { scrollTop, offsetHeight, scrollHeight } = e.target;
    if (!loading && toInteger(scrollTop) + toInteger(offsetHeight) === scrollHeight) {
      if (data.cursors?.next) {
        void loadMore();
      }
    }
  };

  const options: { value: string; label: any; disabled?: boolean }[] = data.services.map(
    (item) => ({
      value: item.id,
      label: item.name,
    })
  );

  const getParams = () => {
    if (!paymentServiceId || isEmpty(data.services)) return [];
    const p = data.services.find((item) => item.id === paymentServiceId)?.parameters;
    if (!p || isEmpty(p)) return [];
    return Object.keys(p)
      .map((key: string) => ({ id: key, ...p[key] }))
      .sort((a, b) => {
        return (a.index || 0) - (b.index || 0);
      });
  };

  useEffect(() => {
    if (data && form) {
      const currService = data.services.find((item) => item.id === paymentServiceId);
      if (currService) {
        const p = currService.parameters;
        form.setFields([{ name: [...source, 'serviceName'], value: currService.name }]);

        if (p) {
          Object.keys(p)
            .map((key: string) => ({ id: key, ...p[key] }))
            .forEach((item) => {
              const { name, id } = item;
              form.setFields([{ name: [...source, 'parameters', id, 'name'], value: name }]);
            });
        }
      }
    }
  }, [form, data, source, paymentServiceId]);

  // restore form state after beneficiary type change
  useEffect(() => {
    if (form) {
      const { nominatedAccounts } = form.getFieldsValue();
      const na = isEmpty(nominatedAccounts) ? [{}] : nominatedAccounts;
      const naType = na[0].id;
      if (naType && !paymentServiceId && isEmpty(data.services)) {
        setPaymentServiceId(naType);
        void handleSearch();
      }
    }
  }, [data.services, form, handleSearch, paymentServiceId]);

  const paramsArr = getParams().sort(
    (param1, param2) => (param1?.index ?? 0) - (param2?.index ?? 0)
  );
  return (
    <>
      <Form.Item
        name={[...source, 'serviceId']}
        label={t('transferType')}
        rules={[{ required: true }]}
      >
        <Select
          options={
            loading ? options.concat([{ value: '', label: <Spin />, disabled: true }]) : options
          }
          onChange={(value: string) => setPaymentServiceId(value)}
          onSearch={handleSearch}
          showSearch
          allowClear
          onClear={handleSearch}
          style={fullWidth}
          placeholder={t('selectTransferType')}
          onPopupScroll={onScroll}
        />
      </Form.Item>
      <Form.Item name={[...source, 'serviceName']} noStyle>
        <Input type="hidden" />
      </Form.Item>
      {paramsArr.length === 0 && loading ? (
        <Spin />
      ) : (
        paramsArr.map((item) => {
          const { id, name, mandatory, type, format, hint, enum: options } = item;
          const getRules = () => {
            const rules = [];
            if (mandatory) rules.push({ required: true });
            if (format === 'PHONE') {
              rules.push({ validator: phoneValidation, message: hint });
            } else if (format?.indexOf('regex:') === 0) {
              rules.push({ validator: regexValidation(format.substring(6)), message: hint });
            }
            return rules;
          };
          return (
            <Fragment key={id}>
              <Form.Item name={[...source, 'parameters', id, 'name']} noStyle>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item
                name={[...source, 'parameters', id, 'value']}
                label={name}
                rules={getRules()}
              >
                {(() => {
                  switch (type) {
                    case 'ENUM':
                      return (
                        <Select
                          style={fullWidth}
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          options={options!.map((option) => {
                            return { value: option.value, label: option.name };
                          })}
                        />
                      );
                    case 'NUMBER':
                      return <InputNumber style={fullWidth} />;
                    case 'DATE':
                      return <DateInput style={fullWidth} />;
                    default:
                      return format === 'PHONE' ? (
                        <PhoneInput />
                      ) : format === 'COUNTRY' ? (
                        <CountryInput />
                      ) : (
                        <Input />
                      );
                  }
                })()}
              </Form.Item>
            </Fragment>
          );
        })
      )}
    </>
  );
};

const fullWidth = { width: '100%' };

export default TransferTypeForm;
